import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Paginated, ResourceModel } from "../models/generic-resource-model";

export abstract class ResourceService<T extends ResourceModel<T>> {
  constructor(
    private httpClient: HttpClient,
    private tConstructor: { new (m: Partial<T>, ...args: unknown[]): T },
    protected apiUrl: string
  ) {}

  public create(resource: Partial<T> & { toJson: () => T }): Observable<T> {
    return this.httpClient
      .post<T>(`${this.apiUrl}`, resource.toJson())
      .pipe(map((result) => new this.tConstructor(result)));
  }

  public get(): Observable<T[]> {
    return this.httpClient
      .get<T[]>(`${this.apiUrl}`)
      .pipe(map((result) => result.map((i) => new this.tConstructor(i))));
  }

  public getPaginated(
    params?:
      | HttpParams
      | any
      | {
          [param: string]:
            | string
            | number
            | boolean
            | readonly (string | number | boolean)[];
        }
  ): Observable<Paginated<T>> {
    return this.httpClient
      .get<T[]>(`${this.apiUrl}`, {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          let paged: Paginated<T> = {
            pagination: JSON.parse(response.headers.get("x-pagination")),
            body: response.body,
          };
          return paged;
        })
      );
  }

  public getPaginatedById(
    id: number,
    params?:
      | HttpParams
      | any
      | {
          [param: string]:
            | string
            | number
            | boolean
            | readonly (string | number | boolean)[];
        }
  ) {
    return this.httpClient
      .get<T[]>(`${this.apiUrl}/${id}`, {
        observe: "response",
        params,
      })
      .pipe(
        map((response) => {
          let paged: Paginated<T> = {
            pagination: JSON.parse(response.headers.get("x-pagination")),
            body: response.body as T[],
          };
          return paged;
        })
      );
  }

  public getById(id: number, includeProperties: string = null): Observable<T> {
    let uri: string = `${this.apiUrl}/${id}`;
    if (includeProperties) {
      uri += `?includeProperties=${includeProperties}`;
    }
    return this.httpClient
      .get<T>(uri)
      .pipe(map((result) => new this.tConstructor(result)));
  }

  public update(resource: Partial<T> & { toJson: () => T }): Observable<T> {
    return this.httpClient
      .put<T>(
        `${this.apiUrl}/${resource.id ? resource.id : (resource as any).id}`,
        resource.toJson()
      )
      .pipe(map((result) => new this.tConstructor(result)));
  }

  public delete(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.apiUrl}/${id}`);
  }

  public tryDelete<T>(id: number): Observable<T[]> {
    return this.httpClient.delete<T[]>(this.apiUrl + `/try/${id}`);
  }

  public getCustom(endpoint: string): Observable<any> {
    return this.httpClient.get<any>(endpoint);
  }
}
